import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import AppContext from '../../../context/AppContext';
import '../../../assets/css/admin/adminBlogs/admblogform.css';

const DEFAULT_CATEGORIES = [
  { value: 'Food Blogs',         label: 'Food',         icon: '🍽️' },
  { value: 'Event Blogs',        label: 'Event',        icon: '🎪' },
  { value: 'Travel Guide Blogs', label: 'Travel Guide', icon: '🗺️' },
];
const AI_TIMEOUT_MS = 120000; 
const OTHER_DESTINATION_VALUE = '__other__'; 
const Toast = ({ show, message, type }) => {
  if (!show) return null;
  return (
    <div className={`adm-blog-toast adm-blog-toast-${type}`}>
      {message}
    </div>
  );
};

const AdmBlogForm = () => {
  const {
    baseUrl,
    authToken,
    selectedBlog,
    closeBlogForm,
    setBlogs,
    imageBaseUrl,
    getadmin, 
  } = useContext(AppContext);

  const [adminId,          setAdminId]          = useState('');
  const [postTitle,        setPostTitle]        = useState('');
  const [postContent,      setPostContent]      = useState('');
  const [postCategory,     setPostCategory]     = useState('');
  const [postStatus,       setPostStatus]       = useState('draft');
  const [metaTitle,        setMetaTitle]        = useState('');
  const [metaDescription,  setMetaDescription]  = useState('');
  const [metaKeywords,     setMetaKeywords]     = useState('');

  const [imageFile,    setImageFile]    = useState(null);   
  const [imagePreview, setImagePreview] = useState('');     

  const [destinations,      setDestinations]      = useState([]); 
  const [destination,       setDestination]       = useState(''); 
  const [customDestination, setCustomDestination]  = useState(''); 
  const [selectedItem,      setSelectedItem]       = useState('');
  const [otherItems,        setOtherItems]         = useState([]);
  const [imageKeyword,      setImageKeyword]       = useState(''); 
  const [aiLoading,         setAiLoading]          = useState(false);
  const [hasUnsavedDraft,   setHasUnsavedDraft]    = useState(false); 
  const [loading, setLoading] = useState(false);
  const [toast,   setToast]   = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    axios
      .get(`${baseUrl}/admin/view-destination`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then(function (response) {
        if (response.data && response.data.data) {
          setDestinations(response.data.data);
        }
      })
      .catch(function (error) {
        console.warn('Destination list fetch failed:', error);
      });
  }, []);

  useEffect(() => {
    if (selectedBlog) {
      setAdminId(         selectedBlog.jjsubadmin_id   ?? '');
      setPostTitle(       selectedBlog.post_title       ?? '');
      setPostContent(     selectedBlog.post_content     ?? '');
      setPostCategory(    selectedBlog.post_category    ?? '');
      setPostStatus(      selectedBlog.post_status      ?? 'draft');
      setMetaTitle(       selectedBlog.meta_title       ?? '');
      setMetaDescription( selectedBlog.meta_description ?? '');
      setMetaKeywords(    selectedBlog.meta_keywords    ?? '');
      setImagePreview(
        selectedBlog.published_image
          ? `${imageBaseUrl}/${selectedBlog.published_image}`
          : ''
      );
      setImageFile(null); 
    } else {
      resetForm();
      setAdminId(getadmin?.id ?? ''); 
    }
  }, [selectedBlog]);

  const resetForm = () => {
    setAdminId('');
    setPostTitle('');
    setPostContent('');
    setPostCategory('');
    setPostStatus('draft');
    setMetaTitle('');
    setMetaDescription('');
    setMetaKeywords('');
    setImageFile(null);
    setImagePreview('');
    setDestination('');
    setCustomDestination('');
    setSelectedItem('');
    setOtherItems([]);
    setImageKeyword('');
    setHasUnsavedDraft(false);
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 6000);
  };

  const getFinalDestination = () => {
    if (destination === OTHER_DESTINATION_VALUE) {
      return customDestination.trim();
    }
    return destination;
  };

  const generateBlog = (itemOverride) => {
    const finalDestination = getFinalDestination();

    if (!finalDestination) {
      showToast('Please select or type a destination first', 'error');
      return;
    }
    if (!postCategory) {
      showToast('Please select a category first', 'error');
      return;
    }

    setAiLoading(true);

    axios
      .post(
        `${baseUrl}/admin/blog-autofill`,
        {
          destination: finalDestination,
          category: postCategory,
          selected_item: itemOverride || '',
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
          timeout: AI_TIMEOUT_MS,
        }
      )
      .then(function (response) {
        setAiLoading(false);

        if (!response.data.success) {
          Swal.fire({
            icon: 'error',
            title: 'Generation failed',
            text: response.data.message || 'AI generation failed',
          });
          return;
        }

        const result = response.data.data ?? {};
        let parsed = result;
        if (typeof result.text === 'string') {
          try {
            const cleaned = result.text.replace(/```json|```/g, '').trim();
            parsed = JSON.parse(cleaned);
          } catch (parseError) {
            console.error('Failed to parse AI response text:', parseError);
            parsed = {};
          }
        }
        setPostTitle(       parsed.post_title       ?? '');
        setPostContent(     parsed.post_content     ?? '');
        setMetaTitle(       parsed.meta_title       ?? '');
        setMetaDescription( parsed.meta_description ?? '');
        setMetaKeywords(    parsed.meta_keywords    ?? '');
        setSelectedItem(    parsed.selected_item    ?? '');
        setOtherItems(      parsed.other_items      ?? []);
        setImageKeyword(    parsed.image_search_keyword ?? '');
        setHasUnsavedDraft(true); 

        Swal.fire({
          icon: 'success',
          title: 'Blog generated!',
          timer: 1400,
          showConfirmButton: false,
        });
      })
      .catch(function (error) {
        setAiLoading(false);
        console.error('AutoFill error:', error);

        let errorMessage = 'Unable to connect to AI service.';

        if (error.code === 'ECONNABORTED') {
          errorMessage = 'AI response is taking too long, please try again.';
        } else if (error.response) {
          console.error('Backend error body:', error.response.data);
          errorMessage =
            error.response.data?.message ||
            error.response.data?.error   ||
            `Server error (${error.response.status})`;
        } else if (error.request) {
          errorMessage = 'No response from server. Check your connection.';
        }

        Swal.fire({
          icon: 'error',
          title: 'Generation failed',
          text: errorMessage,
        });
      });
  };

  const confirmBeforeGenerate = (itemOverride) => {
    if (!hasUnsavedDraft) {
      generateBlog(itemOverride);
      return;
    }

    Swal.fire({
      icon: 'question',
      title: 'You have an unsaved blog draft',
      text: 'Do you want to save it before generating the next one, or discard it?',
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: 'Save & Continue',
      denyButtonText: 'Discard & Continue',
      cancelButtonText: 'Cancel',
    }).then(function (result) {
      if (result.isConfirmed) {
        saveBlog(function () {
          generateBlog(itemOverride);
        });
      } else if (result.isDenied) {
        generateBlog(itemOverride);
      }
    });
  };

  const handleGenerateClick    = () => confirmBeforeGenerate('');
  const handleSuggestionClick  = (item) => confirmBeforeGenerate(item);
  const handleImageChange = (e) => {
  const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showToast('Image size must be less than 2MB', 'error');
      return;
    }

    setImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const saveBlog = (onSuccess) => {
    if (!postTitle.trim() || !postContent.trim() || !postCategory) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing fields',
        text: 'Please fill all required fields (Title, Content, and Category).',
      });
      return;
    }

    const payload = new FormData();
    payload.append('jjsubadmin_id',    adminId);
    payload.append('post_title',       postTitle);
    payload.append('post_content',     postContent);
    payload.append('post_category',    postCategory);
    payload.append('post_status',      postStatus);
    payload.append('meta_title',       metaTitle);
    payload.append('meta_description', metaDescription);
    payload.append('meta_keywords',    metaKeywords);
    if (imageFile) payload.append('published_image', imageFile);

    const endpoint = selectedBlog
      ? `${baseUrl}/admin/update-blog/${selectedBlog.id}`
      : `${baseUrl}/admin/add-blog`;

    const headers = {
      Authorization:  `Bearer ${authToken}`,
      'Content-Type': 'multipart/form-data',
    };

    setLoading(true);

    axios
      .post(endpoint, payload, { headers })
      .then(function (response) {
        const successMsg = selectedBlog
          ? 'Blog updated successfully!'
          : 'Blog created successfully!';

        Swal.fire({
          icon: 'success',
          title: successMsg,
          timer: 1600,
          showConfirmButton: false,
        });

        return axios
          .get(`${baseUrl}/admin/view-blog`, { headers: { Authorization: `Bearer ${authToken}` } })
          .then(function (viewResponse) {
            if (viewResponse.data && viewResponse.data.data) {
              setBlogs(viewResponse.data.data);
            }
          })
          .catch(function (refreshError) {
            console.warn('Blog list refresh failed:', refreshError);
          });
      })
      .then(function () {
        setLoading(false);
        setHasUnsavedDraft(false); 

        if (selectedBlog) {
          closeBlogForm();
          return;
        }
        setPostTitle('');
        setPostContent('');
        setPostStatus('draft');
        setMetaTitle('');
        setMetaDescription('');
        setMetaKeywords('');
        setImageFile(null);
        setImagePreview('');
        setImageKeyword('');

        if (onSuccess) onSuccess();
      })
      .catch(function (error) {
        setLoading(false);

        let errorMessage = 'Unable to connect to server';

        if (error.response) {
          console.error('Server error:', error.response.status, error.response.data);
          errorMessage =
            error.response.data?.message ||
            error.response.data?.error   ||
            `Server error (${error.response.status})`;
        } else if (error.request) {
          console.error('No response received:', error.request);
          errorMessage = 'No response from server. Check your connection.';
        } else {
          console.error('Request setup error:', error.message);
          errorMessage = error.message || 'Unexpected error occurred';
        }
        Swal.fire({
          icon: 'error',
          title: selectedBlog ? 'Update failed' : 'Creation failed',
          text: errorMessage,
        });
      });
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    saveBlog();
  };
  return (
    <div className="adm-blog-form-page">

      <Toast show={toast.show} message={toast.message} type={toast.type} />

      <div className="adm-blog-form-container" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="adm-blog-form-header">
          <h2>{selectedBlog ? 'Edit Blog' : 'Create New Blog'}</h2>
          <button className="adm-blog-btn-close" onClick={closeBlogForm}>
            ✕ Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="adm-blog-form">
          <input type="hidden" value={adminId} readOnly />
          {!selectedBlog && (
            <div className="adm-blog-ai-section">
              <h3>✨ AI Blog Generator</h3>
              <div className="adm-blog-form-row">
                <div className="adm-blog-form-group">
                  <label>Destination *</label>
                  <select
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    className="adm-blog-form-select"
                  >
                    <option value="">Select destination</option>
                    {destinations.map((dest) => (
                      <option key={dest.id} value={dest.destination_name}>
                        {dest.destination_name}
                      </option>
                    ))}
                    <option value={OTHER_DESTINATION_VALUE}>Other (type your own)</option>
                  </select>

                  {destination === OTHER_DESTINATION_VALUE && (
                    <input
                      type="text"
                      value={customDestination}
                      onChange={(e) => setCustomDestination(e.target.value)}
                      placeholder="Enter destination name..."
                      className="adm-blog-form-input"
                      style={{ marginTop: '8px' }}
                    />
                  )}
                </div>

                <div className="adm-blog-form-group">
                  <label>Blog Category *</label>
                  <div className="adm-blog-category-pills">
                    {DEFAULT_CATEGORIES.map((cat) => (
                      <button
                        type="button"
                        key={cat.value}
                        className={`adm-blog-category-pill ${postCategory === cat.value ? 'active' : ''}`}
                        onClick={() => setPostCategory(cat.value)}
                      >
                        {cat.icon} {cat.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="adm-blog-btn-generate"
                onClick={handleGenerateClick}
                disabled={aiLoading}
              >
                {aiLoading ? '⏳ Generating Blog...' : '✨ Generate AI Blog'}
              </button>

              {imageKeyword && (
                <p className="adm-blog-image-hint">
                  💡 Suggested image search: <strong>{imageKeyword}</strong>
                </p>
              )}

              {selectedItem && (
                <div className="adm-blog-suggestions">
                  <div className="adm-blog-selected-item">
                    Selected: <strong>{selectedItem}</strong>
                  </div>

                  {otherItems.length > 0 && (
                    <>
                      <div className="adm-blog-suggestions-label">Other suggestions</div>
                      <div className="adm-blog-suggestion-chips">
                        {otherItems.map((item) => (
                          <button
                            type="button"
                            key={item}
                            className="adm-blog-suggestion-chip"
                            onClick={() => handleSuggestionClick(item)}
                            disabled={aiLoading}
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="adm-blog-form-group">
            <label>Post Title *</label>
            <input
              type="text"
              value={postTitle}
              onChange={(e) => setPostTitle(e.target.value)}
              placeholder="Enter blog title..."
              className="adm-blog-form-input"
              required
            />
          </div>

          <div className="adm-blog-form-group">
            <label>Post Content *</label>
            <textarea
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              placeholder="Write your blog content here..."
              rows={6}
              className="adm-blog-form-textarea"
              required
            />
          </div>

          <div className="adm-blog-form-row">
            <div className="adm-blog-form-group">
              <label>Post Category *</label>
              <select
                value={postCategory}
                onChange={(e) => setPostCategory(e.target.value)}
                className="adm-blog-form-select"
                required
              >
                <option value="">Select a category</option>
                {DEFAULT_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.icon} {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="adm-blog-form-group">
              <label>Post Status *</label>
              <select
                value={postStatus}
                onChange={(e) => setPostStatus(e.target.value)}
                className="adm-blog-form-select"
              >
                <option value="draft">📝 Draft</option>
                <option value="published">✅ Published</option>
              </select>
            </div>
          </div>

          <div className="adm-blog-form-group">
            <label>Published Image</label>
            <div className="adm-blog-image-upload-area">
              {imagePreview ? (
                <div className="adm-blog-image-preview">
                  <img src={imagePreview} alt="Preview" />
                  <button
                    type="button"
                    className="adm-blog-btn-remove-image"
                    onClick={handleRemoveImage}
                  >
                    ✕ Remove
                  </button>
                </div>
              ) : (
                <label className="adm-blog-upload-label">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    hidden
                  />
                  <div className="adm-blog-upload-placeholder">
                    <span className="adm-blog-upload-icon">📷</span>
                    <p>Click to upload image</p>
                    <small>Max 2MB · Recommended: 800×600px</small>
                  </div>
                </label>
              )}
            </div>
          </div>

          <div className="adm-blog-form-group">
            <label>Meta Title</label>
            <input
              type="text"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              placeholder="SEO meta title..."
              className="adm-blog-form-input"
            />
          </div>

          <div className="adm-blog-form-group">
            <label>Meta Description</label>
            <textarea
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              placeholder="SEO meta description (max 160 characters)..."
              maxLength={160}
              rows={3}
              className="adm-blog-form-textarea"
            />
            <span className="adm-blog-char-count">{metaDescription.length}/160</span>
          </div>

          <div className="adm-blog-form-group">
            <label>Meta Keywords</label>
            <input
              type="text"
              value={metaKeywords}
              onChange={(e) => setMetaKeywords(e.target.value)}
              placeholder="keyword1, keyword2, keyword3..."
              className="adm-blog-form-input"
            />
          </div>

          <div className="adm-blog-form-actions">
            <button
              type="button"
              className="adm-blog-btn-cancel"
              onClick={closeBlogForm}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="adm-blog-btn-submit"
              disabled={loading}
            >
              {loading
                ? '⏳ Submitting...'
                : selectedBlog ? '💾 Update Blog' : '🚀 Create Blog'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default AdmBlogForm;